import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const IDLE_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'];

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [isEmulating, setIsEmulating] = useState(false);
  const [originalToken, setOriginalToken] = useState(localStorage.getItem('originalToken'));
  const [sessionTimeout, setSessionTimeout] = useState(() => parseInt(localStorage.getItem('sessionTimeout') || '30', 10));
  const [idleWarning, setIdleWarning] = useState(false);
  const idleTimerRef = useRef(null);
  const warningTimerRef = useRef(null);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
    
    // Check if we're in emulation mode
    const emulating = localStorage.getItem('isEmulating') === 'true';
    setIsEmulating(emulating);
  }, [token]);

  // Idle timeout logic
  const resetIdleTimer = useCallback(() => {
    setIdleWarning(false);
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (!token || !user) return;
    const timeoutMs = sessionTimeout * 60 * 1000;
    const warningMs = Math.max(timeoutMs - 60000, timeoutMs * 0.9); // warn 1 min before, or at 90%
    warningTimerRef.current = setTimeout(() => setIdleWarning(true), warningMs);
    idleTimerRef.current = setTimeout(() => { setIdleWarning(false); logout(); }, timeoutMs);
  }, [sessionTimeout, token, user]);

  useEffect(() => {
    if (!token || !user) return;
    resetIdleTimer();
    const handler = () => resetIdleTimer();
    IDLE_EVENTS.forEach(e => window.addEventListener(e, handler, { passive: true }));
    return () => {
      IDLE_EVENTS.forEach(e => window.removeEventListener(e, handler));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    };
  }, [resetIdleTimer, token, user]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password, tokenData) => {
    let access_token, userData, timeout;
    if (tokenData) {
      access_token = tokenData.access_token;
      userData = tokenData.user;
      timeout = tokenData.session_timeout;
    } else {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      access_token = response.data.access_token;
      userData = response.data.user;
      timeout = response.data.session_timeout;
    }
    if (timeout) {
      setSessionTimeout(timeout);
      localStorage.setItem('sessionTimeout', String(timeout));
    }
    localStorage.setItem('token', access_token);
    localStorage.removeItem('originalToken');
    localStorage.removeItem('isEmulating');
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    setIsEmulating(false);
    setOriginalToken(null);
    return userData;
  };

  const logout = async () => {
    // Try to log the logout event before clearing token
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      // Ignore errors - user might already be logged out
      console.log('Logout logging skipped');
    }
    
    localStorage.removeItem('token');
    localStorage.removeItem('originalToken');
    localStorage.removeItem('isEmulating');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    setIsEmulating(false);
    setOriginalToken(null);
  };

  const startEmulation = (newToken, emulatedUser) => {
    // Save original token for returning later
    const currentToken = localStorage.getItem('token');
    localStorage.setItem('originalToken', currentToken);
    localStorage.setItem('token', newToken);
    localStorage.setItem('isEmulating', 'true');
    
    axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    setOriginalToken(currentToken);
    setToken(newToken);
    setUser(emulatedUser);
    setIsEmulating(true);
  };

  const stopEmulation = async () => {
    const savedOriginalToken = localStorage.getItem('originalToken');
    if (savedOriginalToken) {
      localStorage.setItem('token', savedOriginalToken);
      localStorage.removeItem('originalToken');
      localStorage.removeItem('isEmulating');
      
      axios.defaults.headers.common['Authorization'] = `Bearer ${savedOriginalToken}`;
      setToken(savedOriginalToken);
      setOriginalToken(null);
      setIsEmulating(false);
      
      // Fetch the original user data
      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
      } catch (error) {
        console.error('Failed to restore user:', error);
        logout();
      }
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      login, 
      logout, 
      isEmulating,
      startEmulation,
      stopEmulation,
      sessionTimeout,
      setSessionTimeout,
      idleWarning,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
